import React from 'react';
import { MapPin, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getGenericWhatsAppLink } from '../../utils/whatsapp';

// WhatsApp SVG Icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  const whatsappLink = getGenericWhatsAppLink(language);
  const langPrefix = isEnglish ? '/en' : '';

  // Links data with translations
  const quickLinks = isEnglish ? [
    { name: 'Our excursions', href: '#excursions' },
    { name: 'Our packages', href: '#packages' },
    { name: 'How it works', href: '#how-it-works' },
    { name: 'Customer reviews', href: '#testimonials' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Who am I', href: `${langPrefix}/who-am-i`, isLink: true },
  ] : [
    { name: 'Nos excursions', href: '#excursions' },
    { name: 'Nos packages', href: '#packages' },
    { name: 'Comment ça marche', href: '#how-it-works' },
    { name: 'Avis clients', href: '#testimonials' },
    { name: 'FAQ', href: '#faq' },
    { name: 'Qui suis-je', href: '/qui-suis-je', isLink: true },
  ];

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8">
          {/* Brand Column */}
          <div>
            <Link to={langPrefix || '/'} className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 flex items-center justify-center">
                <span className="text-white font-bold text-lg">H</span>
              </div>
              <span className="font-semibold text-lg">Hurghada Excursions</span>
            </Link>
            <p className="text-slate-400 leading-relaxed mb-4">
              {t('footer.description')}
            </p>
            <div className="flex items-center gap-2 text-slate-400">
              <MapPin className="w-4 h-4 text-teal-400" />
              <span>Hurghada, {isEnglish ? 'Egypt' : 'Égypte'}</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.isLink ? (
                    <Link to={link.href} className="text-slate-400 hover:text-teal-400 transition-colors">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-slate-400 hover:text-teal-400 transition-colors">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-lg mb-4">{t('footer.contact')}</h4>
            <div className="space-y-4">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-400 hover:text-emerald-400 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                  <WhatsAppIcon className="w-5 h-5 text-emerald-400" />
                </div>
                <span>WhatsApp</span>
              </a>
              <p className="text-slate-500 text-sm">
                {t('footer.contactText')}
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            {t('footer.copyright')}
          </p>
          <p className="text-slate-500 text-sm flex items-center gap-1">
            {t('footer.madeWith')} <Heart className="w-4 h-4 text-red-400" fill="currentColor" /> {isEnglish ? 'in Hurghada' : 'à Hurghada'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
